/**
 * OpenSearchFieldMapper - Utility for parsing and working with OpenSearch field mappings
 *
 * This utility provides methods to parse OpenSearch mappings, determine field types,
 * and generate field options for UI components like dropdowns.
 */

import type { SearchFieldUiHints as UiHints } from 'tio-data/data/resources/search.ext';
import { snakeToCamelCase } from 'tio-common/utils/string-case-conversion';
import {
  stripTypeSuffixes,
  getFieldGroup as sharedGetFieldGroup,
  generateColumnLabel,
  generateContextualLabel,
} from './label-generation';

export type { UiHints };

export interface FieldOption {
  key: string;
  label: string;
  type: string;
  group?: string; // Category/section for organizing column selection
  /** TAS field province: 'PROGRAM' | 'APPLICATION' | 'COURSE' | 'PAYMENT' | null */
  province?: string | null;
  isDate?: boolean;
  isTimestamp?: boolean;
  isAggregatable?: boolean; // True if field can be aggregated (e.g., for reporting or analytics)
  adminOnly?: boolean; // True if field should only be shown in admin contexts
  // Capability flags from backend /api/v2/search/fields
  isFilterable?: boolean;
  isSortable?: boolean;
  isGroupable?: boolean; // Maps to backend "aggregate" capability
  isRangeQueryable?: boolean;
  isFullTextSearchable?: boolean;
  isCustomField?: boolean;
  semanticRole?: string;
  reportDatatype?: string;
  enumValues?: string[];
  /** UI rendering hints from the backend (filter widget type, multi-select, etc.) */
  uiHints?: UiHints;
  componentTypeHint?: string;
  // Enhanced /fields endpoint properties (PR #7175)
  /** Semantic UI type: date, currency, boolean, enum, integer, text */
  displayType?: string;
  /** True if field stores cent amounts (divide by 100 for display) */
  currencyCents?: boolean;
  /** Whether this field is displayable as a table column */
  isDisplayable?: boolean;
  /** Field visibility classification from the API */
  visibility?: string;
  /** Exact leaf key for custom field lookup in search results */
  canonicalLeafKey?: string;
  /** snake_case canonical path */
  path?: string;
  /** camelCase key for looking up values in search result documents */
  resultKey?: string;
  /** camelCase path for search queries */
  searchPath?: string;
  /** camelCase path for filter queries */
  filterPath?: string;
  /** Whether this field is a default column for the dataset */
  isDefaultColumn?: boolean;
  /** Business line: TAS, PSLF, SYF, SLR, Core */
  product?: string;
  /** Entity area: Program, Application, Employee, etc. */
  domain?: string;
  /** Legacy field name variants */
  aliases?: string[];
  /** Source Rails model */
  fieldableType?: string;
  /** Query construction hints from the backend /fields API */
  queryHints?: {
    /** Primary OS clause type for filtering: term, terms, match, range, or nested */
    filterClause?: 'term' | 'terms' | 'match' | 'range' | 'nested';
    /** Exact field path for sort, e.g. snake_case or with .keyword suffix */
    sortField?: string;
    /** Whether field can be used for collapse */
    collapseEligible?: boolean;
    /** unmapped_type value for sort on fields that may not exist in all indices */
    unmappedType?: string;
    /** Subfield surface: e.g. .keyword, .analyzed */
    subfields?: Array<{ path: string; suffix: string; type: string }>;
  };
  /**
   * Exact field path for OpenSearch sort clauses.
   * Derived from queryHints.sortField, falling back to path then key.
   * Use this in sort queries rather than deriving from the camelCase key.
   */
  sortPath?: string;
}

export interface OpenSearchMapping {
  [indexName: string]: {
    mappings: {
      properties: {
        [fieldName: string]: {
          type: string;
          fields?: Record<string, { type: string }>;
          properties?: Record<string, unknown>;
        };
      };
    };
  };
}

export class OpenSearchFieldMapper {
  /**
   * List of fields that should only be shown in admin contexts
   * These are typically configuration or workflow fields that are useful for admins
   * but not needed for employee self-service
   */
  private static readonly ADMIN_ONLY_FIELDS = new Set([
    'approverCanEditApplication',
    'programApprovalRequired',
    'courseApprovalRequired',
    'courseGradeRequired',
    'evidenceApprovalRequired',
    'indexedAt',
    'documentCreatedAt',
    // Add other admin-only fields here
  ]);

  private static readonly COMPONENT_HINT_FALLBACKS: Array<{
    componentType: string;
    patterns: RegExp[];
  }> = [
    {
      componentType: 'partner_search',
      patterns: [
        /tas_program_instance_lnp_partner_name_keyword/i,
        /(^|[_.])lpn([_.]|$)/i,
        /(^|[_.])lnp([_.]|$)/i,
        /learning.?partner/i,
      ],
    },
  ];

  // Label mappings are now consolidated in label-generation.ts

  /**
   * Remove OpenSearch type suffixes from a field name to get the base field name.
   * Delegates to shared stripTypeSuffixes in label-generation.ts.
   *
   * @example
   * removeFieldTypeSuffixes('employeeNameKeyword') // => 'employeeName'
   * removeFieldTypeSuffixes('createdAtDate') // => 'createdAt'
   * removeFieldTypeSuffixes('name.keyword') // => 'name'
   */
  static removeFieldTypeSuffixes(fieldName: string): string {
    return stripTypeSuffixes(fieldName);
  }

  /**
   * Check if a field is admin-only
   */
  static isAdminOnlyField(fieldName: string): boolean {
    const baseFieldName = this.removeFieldTypeSuffixes(fieldName);
    return this.ADMIN_ONLY_FIELDS.has(baseFieldName);
  }

  /**
   * Parse OpenSearch mapping data into field options suitable for UI components
   *
   * NOTE: As of the backend change, field names are returned in camelCase (e.g., 'employeeNameKeyword')
   * This matches the format of response data, so no conversion is needed when reading item properties.
   */
  static parseMapping(mappingData: OpenSearchMapping): FieldOption[] {
    if (!mappingData || typeof mappingData !== 'object') {
      console.warn('Invalid mapping data provided');
      return [];
    }

    const fields: FieldOption[] = [];

    // Get the first index mapping (assuming single index for now)
    const indexKey = Object.keys(mappingData)[0];
    if (!indexKey || !mappingData[indexKey]?.mappings?.properties) {
      console.warn('No valid mapping properties found');
      return [];
    }

    const properties = mappingData[indexKey].mappings.properties;

    // Parse each field in the mapping
    // Field names are now in camelCase (e.g., 'employeeNameKeyword', 'payrollId')
    Object.entries(properties).forEach(([fieldName, fieldConfig]) => {
      if (OpenSearchFieldMapper.isFieldSuitableForAggregation(fieldName, fieldConfig)) {
        const label = OpenSearchFieldMapper.formatFieldLabel(fieldName);
        const isAdminOnly = OpenSearchFieldMapper.isAdminOnlyField(fieldName);
        const group = isAdminOnly
          ? 'Admin Configuration'
          : OpenSearchFieldMapper.getFieldGroup(fieldName);
        const isDate = fieldConfig.type === 'date';
        const isTimestamp =
          fieldConfig.type !== 'date' && OpenSearchFieldMapper.isTimestampField(fieldName);

        // For text fields with keyword sub-fields, only include the keyword version
        if (fieldConfig.type === 'text' && fieldConfig.fields?.keyword) {
          fields.push({
            key: `${fieldName}.keyword`,
            label, // Use clean label without technical suffixes
            type: 'keyword',
            group,
            isDate: false,
            isTimestamp: false,
            adminOnly: isAdminOnly,
          });
        } else {
          // For non-text fields or text fields without keyword sub-fields
          fields.push({
            key: fieldName,
            label,
            type: fieldConfig.type,
            group,
            isDate,
            isTimestamp,
            adminOnly: isAdminOnly,
          });
        }
      }
    });

    // Sort fields alphabetically by label
    return fields.sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Determine if a field is suitable for aggregation
   */
  static isFieldSuitableForAggregation(
    fieldName: string,
    fieldConfig: { type: string; fields?: Record<string, unknown> }
  ): boolean {
    const { type } = fieldConfig;

    // Skip internal/system fields
    if (fieldName.startsWith('_') || fieldName.includes('embedding')) {
      return false;
    }

    // Include these types for aggregation
    const goodTypes = ['keyword', 'boolean', 'date', 'long', 'integer', 'short', 'byte'];
    if (goodTypes.includes(type)) {
      return true;
    }

    // Include text fields that have a keyword subfield
    if (type === 'text' && fieldConfig.fields?.keyword) {
      return true;
    }

    return false;
  }

  /**
   * Check if a field name suggests it's a timestamp field
   */
  static isTimestampField(fieldName: string): boolean {
    const timestampPatterns = [
      /at$/i, // ends with 'at' (createdAt, updatedAt, etc.)
      /time/i, // contains 'time'
      /date/i, // contains 'date'
      /_at$/i, // ends with '_at'
      /_date$/i, // ends with '_date'
      /_time$/i, // ends with '_time'
    ];

    return timestampPatterns.some((pattern) => pattern.test(fieldName));
  }

  /**
   * Format field names into readable labels with context awareness.
   * Delegates to shared label-generation.ts functions.
   *
   * Provides two label formats:
   * - Short labels for column selection UI (strips all prefixes)
   * - Full labels for table headers (keeps necessary context)
   *
   * @param fieldName - The OpenSearch field name in camelCase
   * @param options - Formatting options
   * @returns Formatted label string
   */
  static formatFieldLabel(
    fieldName: string,
    options: { keepContext?: boolean; group?: string } = {}
  ): string {
    if (options.keepContext) {
      return generateContextualLabel(fieldName);
    }
    return generateColumnLabel(fieldName);
  }

  /**
   * Get the group/category for a field (for organizing column selection).
   * Delegates to shared getFieldGroup in label-generation.ts.
   */
  static getFieldGroup(fieldName: string): string {
    return sharedGetFieldGroup(fieldName);
  }

  /**
   * Resolve preferred component type for a field.
   * Priority:
   * 1) Explicit backend hint (`uiHint.componentType`)
   * 2) Local key-based fallback patterns (backwards compatibility)
   */
  static resolveComponentTypeHint(fieldName: string, explicitHint?: string): string | undefined {
    const normalizedHint = explicitHint?.trim();
    if (normalizedHint) {
      return normalizedHint;
    }

    for (const entry of this.COMPONENT_HINT_FALLBACKS) {
      if (entry.patterns.some((pattern) => pattern.test(fieldName))) {
        return entry.componentType;
      }
    }

    return undefined;
  }

  /**
   * Get fallback fields for known datasets when mapping fails
   */
  static getFallbackFields(dataset: string): FieldOption[] {
    switch (dataset) {
      case 'companies':
        return [
          { key: 'company_id', label: 'Company ID', type: 'keyword' },
          { key: 'company_name', label: 'Company Name', type: 'keyword' },
          { key: 'company_key', label: 'Company Key', type: 'keyword' },
          { key: 'company_status', label: 'Status', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
          { key: 'updated_at', label: 'Updated At', type: 'date', isDate: true },
        ];
      case 'tas-program-instances':
        return [
          // New standardized field names (Phase 1)
          { key: 'employee_name_keyword', label: 'Employee Name', type: 'text' },
          {
            key: 'tas_program_template_field_program_name_keyword',
            label: 'Program Name',
            type: 'text',
          },
          {
            key: 'tas_program_template_field_program_specialization_keyword',
            label: 'Program Specialization',
            type: 'text',
          },
          {
            key: 'tas_program_instance_created_at_date',
            label: 'Program Created At',
            type: 'date',
            isDate: true,
          },
          { key: 'tas_program_instance_id_keyword', label: 'Program Instance ID', type: 'keyword' },
          { key: 'tas_program_instance_state_keyword', label: 'Program State', type: 'keyword' },
          { key: 'tas_program_template_id_keyword', label: 'Program Template ID', type: 'keyword' },
          { key: 'tas_program_template_type_keyword', label: 'Program Type', type: 'keyword' },
          {
            key: 'tas_program_template_field_voucher_program_boolean',
            label: 'Voucher Program',
            type: 'boolean',
          },

          // Other fields
          { key: 'application_state', label: 'Application State', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
          { key: 'updated_at', label: 'Updated At', type: 'date', isDate: true },
          { key: 'employee_id', label: 'Employee ID', type: 'keyword' },
        ];
      case 'pslf-forms':
        return [
          { key: 'pslf_form_status_keyword', label: 'Form Status', type: 'keyword' },
          { key: 'pslf_form_type_keyword', label: 'Form Type', type: 'keyword' },
          {
            key: 'pslf_form_borrower_name_keyword_analyzable',
            label: 'Borrower Name',
            type: 'text',
          },
          {
            key: 'pslf_form_employer_name_keyword_analyzable',
            label: 'Employer Name',
            type: 'text',
          },
          {
            key: 'pslf_form_created_at_date',
            label: 'Created At',
            type: 'date',
            isDate: true,
          },
          {
            key: 'pslf_form_updated_at_date',
            label: 'Updated At',
            type: 'date',
            isDate: true,
          },
        ];
      case 'syf-match-participants':
        return [
          {
            key: 'syf_match_participant_person_name_text',
            label: 'Participant Name',
            type: 'text',
            isGroupable: false,
          },
          {
            key: 'syf_match_participant_company_name_keyword',
            label: 'Company Name',
            type: 'keyword',
          },
          {
            key: 'syf_match_participant_enrollment_state_keyword',
            label: 'Enrollment State',
            type: 'keyword',
          },
          {
            key: 'syf_match_participant_plan_name_text',
            label: 'Plan Name',
            type: 'text',
            isGroupable: false,
          },
          {
            key: 'syf_match_participant_created_at_date',
            label: 'Created At',
            type: 'date',
            isDate: true,
          },
        ];
      case 'slr-contribution-plan-participants':
        return [
          {
            key: 'slr_contribution_plan_participant_first_name_text',
            label: 'First Name',
            type: 'text',
            isGroupable: false,
          },
          {
            key: 'slr_contribution_plan_participant_last_name_text',
            label: 'Last Name',
            type: 'text',
            isGroupable: false,
          },
          {
            key: 'slr_contribution_plan_participant_company_name_keyword',
            label: 'Company Name',
            type: 'keyword',
          },
          {
            key: 'slr_contribution_plan_participant_enrollment_status_keyword',
            label: 'Enrollment Status',
            type: 'keyword',
          },
          {
            key: 'slr_contribution_plan_participant_plan_name_text',
            label: 'Plan Name',
            type: 'text',
            isGroupable: false,
          },
          {
            key: 'slr_contribution_plan_participant_created_at_date',
            label: 'Created At',
            type: 'date',
            isDate: true,
          },
        ];
      case 'syf-match-payments':
        return [
          {
            key: 'syf_match_payment_transaction_date_date',
            label: 'Transaction Date',
            type: 'date',
            isDate: true,
          },
          {
            key: 'syf_match_participant_person_name_text',
            label: 'Participant Name',
            type: 'text',
            isGroupable: false,
          },
          {
            key: 'syf_match_participant_company_name_keyword',
            label: 'Company Name',
            type: 'keyword',
          },
          { key: 'syf_match_participant_plan_code_keyword', label: 'Plan Code', type: 'keyword' },
          {
            key: 'syf_match_participant_benefit_year_integer',
            label: 'Benefit Year',
            type: 'integer',
          },
          {
            key: 'syf_match_payment_transaction_amount_integer',
            label: 'Transaction Amount',
            type: 'integer',
          },
          {
            key: 'syf_match_payment_payment_qualified_status_keyword',
            label: 'Qualified Status',
            type: 'keyword',
          },
          {
            key: 'syf_match_payment_payment_certification_status_keyword',
            label: 'Certification Status',
            type: 'keyword',
          },
        ];
      case 'syf-match-participant-benefit-years':
        return [
          {
            key: 'syf_match_participant_benefit_year_person_name_text',
            label: 'Participant Name',
            type: 'text',
            isGroupable: false,
          },
          {
            key: 'syf_match_participant_benefit_year_company_name_keyword',
            label: 'Company Benefit',
            type: 'keyword',
          },
          {
            key: 'syf_match_participant_benefit_year_plan_code_keyword',
            label: 'Plan Code',
            type: 'keyword',
          },
          {
            key: 'syf_match_participant_benefit_year_integer',
            label: 'Benefit Year',
            type: 'integer',
          },
          {
            key: 'syf_match_participant_benefit_year_total_payment_count_integer',
            label: 'Payment Count',
            type: 'integer',
          },
          {
            key: 'syf_match_participant_benefit_year_total_paid_amount_integer',
            label: 'Total Paid Amount',
            type: 'integer',
          },
          {
            key: 'syf_match_participant_benefit_year_completion_bucket_keyword',
            label: 'Completion Bucket',
            type: 'keyword',
          },
        ];
      case 'tas-program-templates':
        return [
          { key: 'tas_program_template_name', label: 'Program Name', type: 'keyword' },
          { key: 'tas_program_template_type', label: 'Program Type', type: 'keyword' },
          { key: 'company_id', label: 'Company ID', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
          { key: 'updated_at', label: 'Updated At', type: 'date', isDate: true },
        ];
      case 'people':
        return [
          { key: 'company_name', label: 'Company Name', type: 'keyword' },
          { key: 'job_title', label: 'Job Title', type: 'keyword' },
          { key: 'department', label: 'Department', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
        ];
      default:
        return [
          { key: 'id', label: 'ID', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
        ];
    }
  }

  /**
   * Get the default group-by field for a given dataset.
   * Returns camelCase field names (FE standard) matching the fields API response.
   */
  static getDefaultField(dataset: string): string {
    switch (dataset) {
      case 'companies':
        return 'companyName';
      case 'tas-program-instances':
        return 'tasApplicationStateKeyword';
      case 'tas-program-templates':
        return 'tasProgramTemplateName';
      case 'pslf-forms':
        return 'pslfFormStatusKeyword';
      case 'people':
        return 'companyName';
      case 'syf-match-participants':
        return 'syfMatchParticipantEnrollmentStateKeyword';
      case 'syf-match-payments':
        return 'syfMatchParticipantCompanyNameKeyword';
      case 'syf-match-participant-benefit-years':
        return 'syfMatchParticipantBenefitYearCompanyNameKeyword';
      case 'slr-contribution-plan-participants':
        return 'slrContributionPlanParticipantEnrollmentStatusKeyword';
      default:
        return 'id';
    }
  }

  /**
   * Get all possible aggregation key variations for a field name
   * This helps with backend inconsistencies in key naming
   */
  static getAggregationKeyVariations(fieldName: string): string[] {
    const baseKey = `${fieldName}_agg`;
    return [
      baseKey,
      snakeToCamelCase(baseKey),
      `${fieldName}Agg`,
      snakeToCamelCase(fieldName) + 'Agg',
    ];
  }
}
